/**
 * Drug Registry
 * Central location to register all drugs
 * When you add a new drug, export it here and add to the map
 */

import type { Drug } from "../types";
import { nitrofurantoinDrug } from "./nitrofurantoin.drug";
import { aciclovirDrug } from "./aciclovir.drug";
import { valaciclovirDrug } from "./valaciclovir.drug";
import { flucloxacillinDrug } from "./flucloxacillin.drug";
import { clarithromycinDrug } from "./clarithromycin.drug";
import { erythromycinDrug } from "./erythromycin.drug";
import { phenoxymethylpenicillinDrug } from "./phenoxymethylpenicillin.drug";
import { hydrogenPeroxideDrug } from "./hydrogenPeroxide.drug";
import { fusidicAcidDrug } from "./fusidicAcid.drug";
import { phenazoneLidocaineEarDropsDrug } from "./phenazoneLidocaineEarDrops.drug";
import { amoxicillinDrug } from "./amoxicillin.drug";
import { fluticasoneFuroateNasalSprayDrug } from "./fluticasoneFuroateNasalSpray.drug";
import { mometasoneFuroateNasalSprayDrug } from "./mometasoneFuroateNasalSpray.drug";
import { doxycyclineDrug } from "./doxycycline.drug";
import { tirzepatideDrug } from "./aht/tirzepatide.drug";
import { semaglutideDrug } from "./aht/semaglutide.drug";
import { oralSemaglutideDrug } from "./aht/oral-semaglutide.drug";
import { liraglutideDrug } from "./aht/liraglutide.drug";
import { mysimbaDrug } from "./aht/mysimba.drug";
import { orlistatDrug } from "./aht/orlistat.drug";

/**
 * Map of drugId → Drug config
 * The engine loads a drug by ID from here
 */
export const drugRegistry: Record<string, Drug> = {
  nitrofurantoin: nitrofurantoinDrug,
  aciclovir: aciclovirDrug,
  valaciclovir: valaciclovirDrug,
  flucloxacillin: flucloxacillinDrug,
  clarithromycin: clarithromycinDrug,
  erythromycin: erythromycinDrug,
  phenoxymethylpenicillin: phenoxymethylpenicillinDrug,
  hydrogen_peroxide_cream: hydrogenPeroxideDrug,
  fusidic_acid_cream: fusidicAcidDrug,
  phenazone_lidocaine_ear_drops: phenazoneLidocaineEarDropsDrug,
  amoxicillin: amoxicillinDrug,
  fluticasone_furoate_nasal_spray: fluticasoneFuroateNasalSprayDrug,
  mometasone_furoate_nasal_spray: mometasoneFuroateNasalSprayDrug,
  doxycycline: doxycyclineDrug,
  tirzepatide: tirzepatideDrug,
  semaglutide: semaglutideDrug,
  oral_semaglutide: oralSemaglutideDrug,
  liraglutide: liraglutideDrug,
  mysimba: mysimbaDrug,
  orlistat: orlistatDrug,
};

/**
 * Helper to look up a drug by ID
 * Throws if not found (should never happen in production)
 */
export function getDrug(drugId: string): Drug {
  const drug = drugRegistry[drugId];
  if (!drug) {
    throw new Error(
      `Drug not found: ${drugId}. Register it in drugRegistry before use.`
    );
  }
  return drug;
}

/**
 * Helper to look up a formulation within a drug
 */
export function getFormulation(drugId: string, formulationId: string) {
  const drug = getDrug(drugId);
  const formulation = drug.formulations.find((f) => f.id === formulationId);
  if (!formulation) {
    throw new Error(
      `Formulation ${formulationId} not found in drug ${drugId}`
    );
  }
  return formulation;
}
